import React, { useState, useEffect, useRef } from 'react';
import { Box, Button, Flex, Text } from 'rebass';
import useSWR from 'swr';
import Cookies from 'js-cookie';
import { useHistory } from 'react-router-dom';
import { getRequestOptions, getSWR, post } from 'utils/api';
import ReadyToPurchase from './ReadyToPurchase';
import { FaCircleCheck } from 'react-icons/fa6';
import { useCurrentMember } from 'members/hooks/index';
import { calculatePricing } from 'app/payment/DashboardPrices';
import GiftCertificateDesign from './GiftCertificateDesign';
import PaymentPage from 'app/payment/PaymentPage';
import useLocation from 'app/hooks/location';
import Wrapper from './Wrapper';
import { getAllPackages, getPrefixedEventName } from 'app/form/helper';
import { postMemberEvent } from 'members/api';
import OptionRenderer from './OptionRenderer';
import GiftCertificatePreview from './GiftCertificatePreview';
import GiftCertificateSuccessMessage from './GiftCertificateSuccessMessage';
import { filterUTMParams } from 'members/helper';
import { baseUrl } from 'utils/api-base';
import Banner from 'app/payment/Banner';
import styled from '@emotion/styled';
import { useGiftCertificates } from 'account-dashboard/hooks';

let certificateIdInUrl = null;

const CenteredBannerContainer = styled(Box)`
  position: fixed;
  top: 70vh;
  left: 0;
  right: 0;
  bottom: 100px;
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 9999;
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
`;

const GiftCertificate = () => {
    const [step, setStep] = useState(1);
    const { push, goBack } = useHistory();

    const [orderSummary, setOrderSummary] = useState(null);
    const member = useCurrentMember();
    const { search, state } = useLocation();
    const [email, setEmail] = useState(member?.email);
    const [generatingInvoice, setGeneratingInvoice] = useState(false);

    const [quantity, setRequiresQuantity] = useState(1);
    const [name_of_giver, setNameOfGiver] = useState(null);
    // Prefilled when arriving from the Account Dashboard's "Who are you gifting
    // this to?" step, which has already collected the recipient's name.
    const [name_of_recipient, setNameOfRecipient] = useState(
        () => new URLSearchParams(search).get('recipientName') || null
    );
    const [gift_certificate_style_id, setGiftCertificateStyleId] = useState(null);
    certificateIdInUrl = new URLSearchParams(search).get('certificateId') || null;

    // Seeded when editing an existing certificate from the Account Dashboard, so
    // the design step PATCHes that record instead of creating a second one.
    const [certificateId, setCertificateId] = useState(
        () => certificateIdInUrl || null
    );
    const [selectedPlan, setSelectedPlan] = useState(null);

    // ── Multi-certificate flow ────────────────────────────────────────────────
    // orderQuantity: max certs allowed (from URL param, read once at mount)
    // createdCount:  how many have already been created (from API + local increments)
    const [orderQuantity, setOrderQuantity] = useState(1);
    const [createdCount, setCreatedCount] = useState(0);
    const [isLoadingCount, setIsLoadingCount] = useState(true);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    // Ref guard prevents double-submission if the user clicks twice quickly
    const isSubmittingRef = useRef(false);
    // Ensure orderQuantity is read from the URL only once (not re-read on every
    // step navigation, which changes the search string)
    const orderQuantityInitializedRef = useRef(false);

    // ── Editing an existing certificate ───────────────────────────────────────
    // Arriving from the Account Dashboard's "Edit Certificate" with only a
    // certificateId in the URL: fetch the certificates and prefill from the record
    // that matches it. The request is skipped entirely when not editing.
    const { giftCertificates } = useGiftCertificates({ enabled: !!certificateId });
    const editingCertificate = certificateId
        ? giftCertificates.find(({ id }) => String(id) === String(certificateId))
        : null;
    // Prefill once, so it can't clobber the customer's own edits on a revalidation.
    const hasPrefilledRef = useRef(false);

    useEffect(() => {
        if (!editingCertificate || hasPrefilledRef.current) return;
        hasPrefilledRef.current = true;

        if (editingCertificate.giverName) setNameOfGiver(editingCertificate.giverName);
        if (editingCertificate.recipientName) setNameOfRecipient(editingCertificate.recipientName);
        if (editingCertificate.styleId) setGiftCertificateStyleId(editingCertificate.styleId);
    }, [editingCertificate]);

    const isDisabled = (!name_of_giver || !name_of_recipient || !gift_certificate_style_id)

    function getUrl() {
        let param = `?group=giving`;
        return `/v3/child_journals/product_packages${param}`;
    }

    const { data: mainData } = useSWR(getUrl(), getSWR);
    const { data: mainSurveyQuest } = useSWR(`v3/public/surveys/byb_gift_certificate_purchase_v6`, getSWR);

    const whoDoYouWantToGiftTheBabyBookTo = mainSurveyQuest?.data?.included.find(({ attributes }) => attributes?.['question-text'] === "Who do you want to gift the baby book to?")
    const howOldIsTheBaby = mainSurveyQuest?.data?.included.find(({ attributes }) => attributes?.['question-text'] === "How old is the baby?")

    const whoDoYouWantToGiftTheBabyBookToSurveyData = {
        id: mainSurveyQuest?.data?.data?.id,
        attributes: whoDoYouWantToGiftTheBabyBookTo?.attributes
    }

    const howOldIsTheBabySurveyData = {
        id: mainSurveyQuest?.data?.data?.id,
        attributes: howOldIsTheBaby?.attributes
    }

    const data = mainData?.data

    const calculatedPricing = calculatePricing(data, selectedPlan) || {};
    calculatedPricing.allPackages = getAllPackages(data)

    useEffect(() => {
        if (!calculatedPricing.allPackages?.paidPlans) return;
        const plan = calculatedPricing.allPackages.paidPlans.find(({ description }) => description?.includes('Hardcover'));
        if (plan && !selectedPlan) {
            setSelectedPlan(plan);
        }
    }, [calculatedPricing.allPackages?.paidPlans, selectedPlan])

    async function generateInvoice(postalCode) {
        setGeneratingInvoice(true)
        setOrderSummary(null)
        const res = await post(`/v3/product_packages/${calculatedPricing?.packageId}/invoices` +
            (quantity !== undefined ? `?quantity=${quantity}` : '') +
            (postalCode ? `&postal_code=${postalCode}` : '')
            + (name_of_giver ? `&name_of_giver=${name_of_giver}` : '')
            + (name_of_recipient ? `&name_of_recipient=${name_of_recipient}` : '')
            + (gift_certificate_style_id ? `&gift_certificate_style_id=${gift_certificate_style_id}` : '')
        );

        if (res.status === 200) {
            const lineItems = res?.data?.initial?.data?.relationships?.['line-items']?.data || [];
            const lineItemsDetails = lineItems.map(lineItem => ({
                ...res?.data?.initial?.included.find(({ id, type }) => id === lineItem?.id && type.includes(lineItem?.type))?.attributes,
                id: lineItem?.id,
            }));
            setOrderSummary({
                ...res?.data?.initial?.data?.attributes,
                lineItems: lineItemsDetails,
            });
            setGeneratingInvoice(false)
            return { ...res?.data?.initial?.data?.attributes, ...res?.data?.initial?.included.find(({ id, type }) => id === lineItems?.id && type.includes(lineItems?.type))?.attributes, id: res?.data?.initial?.data?.id }
        }
        else {
            setGeneratingInvoice(false)
            return false
        }
    }

    const handleStepChange = () => goBack();

    useEffect(() => {
        if ((step === 6 || step === 7) && calculatedPricing?.packageId)
            generateInvoice()

    }, [step, calculatedPricing?.packageId, quantity]);

    async function firePostMemberEvent(eventName) {
        postMemberEvent(getPrefixedEventName(eventName))
    }

    function skiptoStep(jumpToStep, eventName) {
        if (typeof jumpToStep === 'number') {
            push(`?step=${jumpToStep}${filterUTMParams(window?.location?.search)}`)
            firePostMemberEvent(eventName || 'save_20_today')
        }
        else {
            if (eventName)
                firePostMemberEvent(eventName)
            push(`?step=${step + 1}${filterUTMParams(window?.location?.search)}`)
        }
        if (showSuccessMessage)
            setShowSuccessMessage(false)
    }

    async function getGiftCertificateDetails() {
        try {
            const queryParams = new URLSearchParams(search);
            const orderId = queryParams.get('orderId');
            if (orderId) {
                const resp = await fetch(`${baseUrl}/v3/shopify_orders/${orderId}/gift_certificates`, getRequestOptions());
                const response = await resp.json();

                if (response?.data === null || response?.errors?.length > 0) {
                    return 0;
                }
                return Array.isArray(response?.data) ? response.data.length : 0;
            }
        } catch (err) {
            return 0;
        }
    }

    useEffect(() => {
        const customerEmail = Cookies.get('customer_email');
        if (customerEmail) setEmail(customerEmail);

        (async () => {
            setIsLoadingCount(true);
            const count = await getGiftCertificateDetails();
            setCreatedCount(count);
            setIsLoadingCount(false);
        })();
    }, []);

    // Called when the user finishes Step 4 (confirms the preview).
    // Re-fetches the server count to stay in sync, then shows the success screen.
    async function handleCertificateCreated() {
        if (isSubmittingRef.current) return;
        isSubmittingRef.current = true;

        const freshCount = await getGiftCertificateDetails();
        // Take whichever is higher — server is authoritative, but if the API
        // hasn't caught up yet, increment locally so the UI is never stale.
        const newCount = Math.max(freshCount, createdCount + 1);
        setCreatedCount(newCount);
        setShowSuccessMessage(true);
        isSubmittingRef.current = false;
    }

    // Resets form state and navigates back to Step 1 for the next certificate.
    function handleStartNext() {
        setNameOfGiver(null);
        setNameOfRecipient(null);
        setGiftCertificateStyleId(null);
        setCertificateId(null);
        setOrderSummary(null);
        setShowSuccessMessage(false);
        push(`/account/gifts`);
    }

    useEffect(() => {
        const queryParams = new URLSearchParams(search);
        if (queryParams.get('certificateId')) {
            setCertificateId(queryParams.get('certificateId'));
            setStep(3);
            return;
        }

        const raw = queryParams.get('orderQuantity');
        // Read orderQuantity once from the landing URL. It must not be re-read
        // on subsequent step navigations because those URLs no longer carry it.
        if (!orderQuantityInitializedRef.current) {
            orderQuantityInitializedRef.current = true;
            const parsed = parseInt(raw, 10);
            if (Number.isFinite(parsed) && parsed > 0) {
                setOrderQuantity(parsed);
            }
        }

        const stepFromUrl = queryParams.get('step');
        if (stepFromUrl) {
            setStep(parseInt(stepFromUrl, 10));
        } else if (!stepFromUrl) {
            firePostMemberEvent('cherish_the_story_of_your_baby')
            push(`?step=${raw === 1 ? 1 : 3}${filterUTMParams(window?.location?.search)}`)
        }
        if (stepFromUrl === 0)
            firePostMemberEvent('intro')
    }, [search])

    // const isAllCreated = !isLoadingCount && createdCount >= orderQuantity;

    function renderheader() {
        return orderQuantity > 1 && (
            <Flex
                justifyContent="center"
                alignItems="center"
                py={3}
                sx={{
                    gap: '10px', mt: '40px', zIndex: 1,
                    ':after': {
                        display: 'block',
                        width: '185px',
                        height: 3,
                        backgroundColor: '#763099',
                        margin: '10px 0px',
                        content: '" "',
                    },
                    ':before': {
                        display: 'block',
                        width: '185px',
                        height: 3,
                        backgroundColor: '#763099',
                        margin: '10px 0px',
                        content: '" "',
                    },
                }}
            >
                <Text
                    fontFamily="Poppins"
                    fontWeight={600}
                    fontSize={15}
                    color="#763099"
                    minWidth='80px'
                    textAlign={'center'}
                >
                    Gift {createdCount + 1} of {orderQuantity}
                </Text>
            </Flex>
        )
    }

    const renderStep = () => {
        switch (step) {
            // case 0:
            //     return (
            //         <Information
            //             onStepDown={() => handleStepChange(step - 1)}
            //             firePostMemberEvent={firePostMemberEvent}
            //             onStepUp={(jumpToStep) => {
            //                 skiptoStep(jumpToStep, 'cherish_the_story_of_your_baby')
            //             }}
            //         />
            //     );
            // case 1:
            //     return (
            //         <ChooseYourPlan
            //             onStepDown={() => handleStepChange(step - 1)}
            //             onStepUp={(jumpToStep) => {
            //                 skiptoStep(jumpToStep, 'who_do_you_want_gift_book_to')
            //             }}
            //         />
            //     );
            case 1:
                return (
                    <OptionRenderer
                        surveyData={whoDoYouWantToGiftTheBabyBookToSurveyData}
                        firePostMemberEvent={firePostMemberEvent}
                        // onStepDown={() => handleStepChange(step - 1)}
                        onStepUp={(jumpToStep) => {
                            skiptoStep(jumpToStep, 'cherish_the_story_of_your_baby')
                        }}
                        renderheader={renderheader}
                    />

                );
            case 2:
                return <OptionRenderer
                    surveyData={howOldIsTheBabySurveyData}
                    firePostMemberEvent={firePostMemberEvent}
                    onStepDown={() => handleStepChange(step - 1)}
                    onStepUp={(jumpToStep) => {
                        skiptoStep(jumpToStep, 'heres_whats_next')
                    }}
                    renderheader={renderheader}
                />
            // case 4:
            //     return <FAQSection
            //         firePostMemberEvent={firePostMemberEvent}
            //         onStepUp={(jumpToStep) => {
            //             skiptoStep(jumpToStep, 'where_should_we_send_receipt')
            //         }}
            //         onStepDown={() => handleStepChange(step - 1)}
            // renderheader={renderheader}    
            // />
            // case 5:
            //     return (
            //         <AddEmail
            //             hideSkip
            //             firePostMemberEvent={firePostMemberEvent}
            //             quantity={quantity}
            //             member={member}
            //             email={email}
            //             setEmail={setEmail}
            //             calculatedPricing={calculatedPricing}
            //             setRequiresQuantity={setRequiresQuantity}
            //             onStepUp={(jumpToStep) => {
            //                 skiptoStep(jumpToStep, 'select_your_gift')
            //             }}
            //             generateInvoice={generateInvoice}
            //             onStepDown={() => handleStepChange(step - 1)}
            //             orderSummary={orderSummary}
            // renderheader={renderheader}        
            // />

            //     )
            // case 6:
            //     return (
            //         <ReadyToPurchase
            //             selectedPlan={selectedPlan}
            //             setSelectedPlan={setSelectedPlan}
            //             allPackages={calculatedPricing.allPackages}
            //             hideSkip
            //             firePostMemberEvent={firePostMemberEvent}
            //             quantity={quantity}
            //             member={member}
            //             email={email}
            //             setEmail={setEmail}
            //             calculatedPricing={calculatedPricing}
            //             setRequiresQuantity={setRequiresQuantity}
            //             onStepUp={(jumpToStep, plan) => {
            //                 // if ((plan || selectedPlan)?.title === "Free") {
            //                 //     firePostMemberEvent('try_it_for_free')
            //                 //     push(`/babys-first-year/get-started?step=8${filterUTMParams(window?.location?.search)}`)
            //                 // } else {
            //                 skiptoStep(jumpToStep, 'review_and_complete_your_order')
            //                 // }
            //             }}
            //             generateInvoice={generateInvoice}
            //             onStepDown={() => handleStepChange(step - 1)}
            //             orderSummary={orderSummary}
            // renderheader={renderheader}        
            // />
            //     )
            // case 7:
            //     return <Wrapper
            //         hideSkip
            //         percentComplete={100}
            //         showFooter={false}
            //     // mainImage={isFromMobile ? mobilePurchaseGiftBFY : purchaseGiftBFY}
            //     >
            //         <PaymentPage
            //             generatingInvoice={generatingInvoice}
            //             isForMyBaby={false}
            //             backText="Back"
            //             onStepUp={() => skiptoStep(undefined, 'thank_you_for_your_purchase')}
            //             onBack={() => handleStepChange(step - 1)}
            //             quantity={quantity}
            //             calculatedPricing={calculatedPricing}
            //             selectedPlan={selectedPlan}
            //             orderSummary={orderSummary}
            //             generateInvoice={generateInvoice}
            //             name_of_recipient={name_of_recipient}
            //             name_of_giver={name_of_giver}
            //             gift_certificate_style_id={gift_certificate_style_id}
            //             firePostMemberEvent={firePostMemberEvent}
            // renderheader={renderheader}        
            // />
            //     </Wrapper>
            // case 8:
            //     return <PaymentSuccess
            //         isForMyBaby={false}
            //         quantity={quantity}
            //         orderSummary={orderSummary}
            //         calculatedPricing={calculatedPricing}
            //         // currentChildJournalId={currentChildJournalId}
            //         // attributes={childjo}
            //         gift_certificate_style_id={gift_certificate_style_id}
            //         onStepDown={() => handleStepChange(6)}
            //         onStepUp={() => skiptoStep(undefined, 'select_your_gift_certificate')}
            // renderheader={renderheader}    
            // />
            case 3:
                return (
                    <GiftCertificateDesign
                        hideSkip
                        onStepUp={(jumpToStep) => {
                            skiptoStep(jumpToStep, 'review_your_gift_certificate')
                        }}
                        onStepDown={certificateIdInUrl ? goBack : undefined}
                        certificateId={certificateId}
                        setCertificateId={setCertificateId}
                        setOrderSummary={setOrderSummary}
                        orderSummary={orderSummary}
                        member={member}
                        firePostMemberEvent={firePostMemberEvent}
                        gift_certificate_style_id={gift_certificate_style_id}
                        setGiftCertificateStyleId={setGiftCertificateStyleId}
                        name_of_recipient={name_of_recipient}
                        setNameOfRecipient={setNameOfRecipient}
                        name_of_giver={name_of_giver}
                        setNameOfGiver={setNameOfGiver}
                        generateInvoice={generateInvoice}
                        isDisabled={isDisabled}
                        renderheader={renderheader}
                    />
                );
            case 4:
                return (
                    <GiftCertificatePreview
                        hideSkip
                        // isAllCreated={isAllCreated}
                        setCreatedCount={createdCount >= orderQuantity ? undefined : setCreatedCount}
                        orderQuantity={orderQuantity}
                        setOrderQuantity={setOrderQuantity}
                        createdCount={createdCount}
                        handleStartNext={handleStartNext}
                        onStepUp={() => {
                            handleCertificateCreated()
                        }}
                        onStepDown={() => {
                            handleStepChange(step - 1)
                        }}
                        certificateId={certificateId}
                        setOrderSummary={setOrderSummary}
                        orderSummary={orderSummary}
                        member={member}
                        gift_certificate_style_id={gift_certificate_style_id}
                        setGiftCertificateStyleId={setGiftCertificateStyleId}
                        name_of_recipient={name_of_recipient}
                        setNameOfRecipient={setNameOfRecipient}
                        name_of_giver={name_of_giver}
                        setNameOfGiver={setNameOfGiver}
                        generateInvoice={generateInvoice}
                        isDisabled={isDisabled}
                        renderheader={renderheader}
                    />
                );

            default:
                return null;
        }
    };

    // if (showPaymentScreen) {
    //     return (
    //         <Wrapper
    //             hideSkip
    //             percentComplete={100}
    //             showFooter={false}
    //         // mainImage={isFromMobile ? mobilePurchaseGiftBFY : purchaseGiftBFY}
    //         >
    //             <PaymentPage
    //                 isForMyBaby={false}
    //                 backText="Back"
    //                 onStepUp={() => {
    //                     setShowPaymentScreen(false)
    //                     firePostMemberEvent('select_your_gift_certificate')
    //                     setStep(12)
    //                 }}
    //                 onBack={() => {
    //                     setShowPaymentScreen(false)
    //                     setStep(11)
    //                 }}
    //                 quantity={quantity}
    //                 calculatedPricing={calculatedPricing}
    //                 orderSummary={orderSummary}
    //                 generateInvoice={generateInvoice}
    //                 name_of_recipient={name_of_recipient}
    //                 name_of_giver={name_of_giver}
    //                 gift_certificate_style_id={gift_certificate_style_id}
    //                 firePostMemberEvent={firePostMemberEvent}
    //             />
    //         </Wrapper>
    //     );
    // }

    // ── Derived ───────────────────────────────────────────────────────────────
    // True once the API count has loaded AND the user has created all certificates.
    // Using >= so that a server count that already exceeds the quota also triggers it.

    // ── Final success page (all certificates created) ─────────────────────────
    // if (isAllCreated) {
    //     return (
    //         <GiftCertificatePreview
    //             hideSkip
    //             onStepUp={() => {}}
    //             onStepDown={() => {}}
    //             certificateId={certificateId}
    //             setOrderSummary={setOrderSummary}
    //             orderSummary={orderSummary}
    //             member={member}
    //             gift_certificate_style_id={gift_certificate_style_id}
    //             setGiftCertificateStyleId={setGiftCertificateStyleId}
    //             name_of_recipient={name_of_recipient}
    //             setNameOfRecipient={setNameOfRecipient}
    //             name_of_giver={name_of_giver}
    //             setNameOfGiver={setNameOfGiver}
    //             generateInvoice={generateInvoice}
    //             isDisabled={isDisabled}
    //         />
    //     );
    // }

    // // ── Per-certificate success message ───────────────────────────────────────
    if (showSuccessMessage) {
        return <Box sx={{
            height: '100vh',
            width: '100vw',
            position: 'fixed',
            top: 0,
            left: 0,
            bg: 'white'
        }}>
            <GiftCertificateSuccessMessage onContinue={handleStartNext} />
        </Box>
    }

    // ── Normal step flow with progress indicator ──────────────────────────────
    return (
        <>
            {/* {showSuccessMessage && (<CenteredBannerContainer>
                <Banner
                    icon={FaCircleCheck}
                    bg={'successBg'}
                    color={'success'}
                    heading={"Gift certificate created successfully!"}
                    size='small'
                    justifyContent="center"
                    alignItems="center"
                    p="16px"
                />
            </CenteredBannerContainer>
            )} */}
            {renderStep()}
        </>
    );
};

export default GiftCertificate;
